import { api } from 'api';
import { defaultProvider, ROUTES } from 'constant';

import { AppThunk } from 'store';
import {
  clearService,
  resetForm,
  selectService,
  selectServiceList,
  setCheckoutData,
  setMoveApptId,
  setProvider,
  setRecommendedProducts,
  setReferalLink,
  setServiceActiveStep,
  setStep
} from 'store/reducers/bookingAppointmentSlice';
import { setLocations } from 'store/reducers/locationSlice';
import {
  setAddonsForService,
  setCategories,
  setError,
  setIsAddonsLoading,
  setIsBookingLoading,
  setIsLoading,
  setIsLoadingBookServicesByParameters,
  setIsProvidersLoading,
  setIsSuggestedServicesLoading,
  setPromoErrorType,
  setProviders,
  setServices,
  setSuggestedServices
} from 'store/reducers/servicesSlice';
import { selectLocation, setRedirectFlow } from 'store/reducers/userSlice';

import { decodeHTMLEntities } from 'utils';
import { isValidStoreIdFromAppt } from 'utils/bookingSearchParams';
import {
  ProviderStepDecision,
  resolveProviderStepDecision
} from 'utils/helpers/providerStep';
import {
  buildSuggestedServicesFetchKey,
  normalizeSuggestedServices,
  shouldShowSuggestedServicesStep
} from 'utils/helpers/suggestedServices';

import {
  AppointmentInterface,
  BookAgainServiceParams,
  BookingParams,
  BookingSteps,
  BookServiceSteps,
  ContentFilter,
  DiscountCode,
  ExtendedAddonInterface,
  LocationFlowType,
  LocationInterface,
  ProductItem,
  PromoErrorType,
  ProviderInterface,
  RecentAppointment,
  ResponseStatusObject,
  SearchParameters,
  SelectedProductType,
  ServiceInterface,
  SuggestedService,
  UpcomingAppointment
} from 'types';

export const getSuggestedServices =
  (
    locationId: number,
    serviceIds: number[]
  ): AppThunk<Promise<SuggestedService[]>> =>
  async (dispatch, getState) => {
    const uniqueSvcIds = Array.from(new Set(serviceIds.filter(Boolean)));
    const fetchKey = buildSuggestedServicesFetchKey(locationId, uniqueSvcIds);
    const {
      suggestedServices = [],
      suggestedServicesFetched = false,
      suggestedServicesFetchKey = ''
    } = getState().services;

    if (
      suggestedServicesFetched &&
      suggestedServicesFetchKey === fetchKey &&
      suggestedServices.length > 0
    ) {
      return suggestedServices;
    }

    if (!locationId || uniqueSvcIds.length === 0) {
      dispatch(setSuggestedServices({ items: [], fetchKey }));
      return [];
    }

    dispatch(setIsSuggestedServicesLoading(true));
    try {
      const { data } = await api.services.getSuggestedSvc(
        locationId,
        uniqueSvcIds
      );
      const suggestions = normalizeSuggestedServices(data);
      dispatch(setSuggestedServices({ items: suggestions, fetchKey }));

      return suggestions;
    } catch (error) {
      console.error(`Error while getting suggested services: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      dispatch(setSuggestedServices({ items: [], fetchKey }));
      return [];
    } finally {
      dispatch(setIsSuggestedServicesLoading(false));
    }
  };

export const findBookableServiceById =
  (
    locationId: number,
    serviceId: number
  ): AppThunk<Promise<ServiceInterface | null>> =>
  async (dispatch, getState) => {
    const servicesStore = getState().services;
    const locationServices = servicesStore.services[locationId];
    if (locationServices) {
      const match = Object.values(locationServices)
        .map((categoryServices) =>
          categoryServices.find((svc: ServiceInterface) => svc.id === serviceId)
        )
        .find(Boolean);
      if (match) {
        return match;
      }
    }

    const typedServices = servicesStore.servicesByType[locationId];
    if (typedServices) {
      const match = Object.values(typedServices)
        .map((list) =>
          list.find((svc: ServiceInterface) => svc.id === serviceId)
        )
        .find(Boolean);
      if (match) {
        return match;
      }
    }

    try {
      const { data } = await api.services.getServicesByLocId(null, locationId);
      const match = (data as ServiceInterface[]).find(
        (svc) => svc.id === serviceId
      );
      if (match) {
        dispatch(
          setServices({
            locationId,
            categoryId: match.typid ?? 0,
            services: data as ServiceInterface[]
          })
        );
      }
      return match ?? null;
    } catch {
      return null;
    }
  };

export const getAddons =
  (
    locationId: number,
    serviceIds: number[] | number
  ): AppThunk<Promise<ExtendedAddonInterface[]>> =>
  async (dispatch) => {
    dispatch(setIsAddonsLoading(true));
    try {
      const serviceIdArray = Array.isArray(serviceIds)
        ? serviceIds
        : [serviceIds];
      const promises = serviceIdArray.map(async (serviceId) => {
        const { data } = await api.services.getAddons(locationId, serviceId);
        const addonsWithParentServiceId = data.map(
          (addon: ExtendedAddonInterface) => ({
            ...addon,
            parentServiceId: serviceId
          })
        );
        dispatch(
          setAddonsForService({
            locationId,
            serviceId,
            addons: addonsWithParentServiceId
          })
        );
        return addonsWithParentServiceId;
      });
      const addonsData = await Promise.all(promises);
      return addonsData.flatMap((addons) => addons);
    } catch (error) {
      console.error(`Error while getting addons: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      return [];
    } finally {
      dispatch(setIsAddonsLoading(false));
    }
  };

export const getProviders =
  (
    locationId: number,
    serviceIds: number[] | number
  ): AppThunk<Promise<ProviderInterface[]>> =>
  async (dispatch) => {
    dispatch(setIsProvidersLoading(true));
    try {
      const serviceIdArray = Array.isArray(serviceIds)
        ? serviceIds
        : [serviceIds];
      const promises = serviceIdArray.map(async (serviceId) => {
        const { data } = await api.services.getProviders(locationId, serviceId);
        dispatch(setProviders({ locationId, serviceId, providers: data }));
        return data;
      });
      const providerArrays = await Promise.all(promises);
      return providerArrays.flat();
    } catch (error) {
      console.error(`Error while getting providers: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      return [];
    } finally {
      dispatch(setIsProvidersLoading(false));
    }
  };

/** Fetch missing provider lists, then decide skip / show / auto-select. */
export const resolveProvidersForBooking =
  (
    locationId: number,
    serviceIds: number[],
    addonIds: number[]
  ): AppThunk<Promise<ProviderStepDecision>> =>
  async (dispatch, getState) => {
    const entityIds = [...serviceIds, ...addonIds];
    const currentProviders = getState().services.providers[locationId];
    const toFetch = entityIds.filter((id) => !currentProviders?.[id]);

    if (toFetch.length) {
      await dispatch(getProviders(locationId, toFetch));
    }

    const providersByEntityId = getState().services.providers[locationId] ?? {};

    return resolveProviderStepDecision(entityIds, providersByEntityId);
  };

export const bookingAppointment =
  (
    params: BookingParams,
    comment: string = ''
    // formId: number | null = null
  ): AppThunk<
    Promise<{
      isSuccess: boolean;
      apptId: number;
      apptCalendarDate: { start: string; end: string };
    }>
  > =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));

      // TODO: remove unitll backend update ip
      // if (formId) {
      //   const clientIp =
      //     typeof window !== 'undefined'
      //       ? localStorage.getItem('client_ip')
      //       : '';
      //   const { data: signData } = await api.forms.signForm({
      //     form_id: formId,
      //     sign_ip: clientIp || ''
      //   });
      //   const isSigned = Boolean(
      //     signData?.success && signData?.signed && signData?.sign_id
      //   );
      //   if (!isSigned) {
      //     const errorMessage = decodeHTMLEntities(
      //       signData?.error_message || 'Unable to sign required form.'
      //     );
      //     dispatch(setError(errorMessage));
      //     return { isSuccess: false, apptId: '' };
      //   }
      // }

      const { data } = await api.services.bookingAppointment(params);
      dispatch(setCheckoutData(data?.data?.[0]));
      if (!data.success) {
        dispatch(setError(decodeHTMLEntities(data.error_message)));
      } else if (comment) {
        await api.services.updateAppointmentComment(
          data.data?.[0]?.appt_id,
          comment
        );
      }
      return {
        isSuccess: data.success,
        apptId: data.data[0]?.appt_id ?? '',
        apptCalendarDate: {
          start: data.data[0]?.appt_start ?? '',
          end: data.data[0]?.appt_end ?? ''
        }
      };
    } catch (error) {
      console.error(`Error while booking appointment: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      return {
        isSuccess: false,
        apptId: '',
        apptCalendarDate: { start: '', end: '' }
      };
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const updateAppointmentComment =
  (appointmentId: number, comment: string): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const { data } = await api.services.updateAppointmentComment(
        appointmentId,
        comment
      );
      return data.success;
    } catch (error) {
      console.error(`Error while update appointment comment: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      return false;
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const validatePromoCode =
  (
    promoCode: string,
    appt: AppointmentInterface
  ): AppThunk<Promise<DiscountCode>> =>
  async (dispatch) => {
    try {
      const { data } = await api.services.validatePromoCode(promoCode, appt);
      return data;
    } catch (error) {
      console.error(`Error while validate promo code: ${error.message}`);
      dispatch(setPromoErrorType(PromoErrorType.DOES_NOT_EXIST));
      return null;
    }
  };

export const bookServiceAgain =
  (service: BookAgainServiceParams, anotherLocationId?: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(resetForm());
      dispatch(setIsBookingLoading(true));
      const locationId = anotherLocationId || service.locid;
      const serviceId = service.svcid;
      const providerId = service.emid;
      const categoryId = service.svc_cat_id;

      const locations = await api.locations.getLocations(
        ContentFilter.ServicesOnly
      );
      const services = await api.services.getServicesByLocId(
        categoryId,
        locationId
      );
      const providers = await api.services.getProviders(locationId, serviceId);
      const categories = await api.services.getCategoriesByLocId(locationId);
      const addons = await api.services.getAddons(locationId, serviceId);
      dispatch(setLocations(locations.data));
      dispatch(
        setServices({ locationId, categoryId, services: services.data })
      );
      dispatch(
        setProviders({ locationId, serviceId, providers: providers.data })
      );
      dispatch(setCategories({ locationId, categories: categories.data }));
      dispatch(
        setAddonsForService({
          locationId,
          serviceId,
          addons: addons.data
        })
      );

      const selectedLocation = locations.data.find(
        (item: LocationInterface) => item.id === locationId
      );
      const selectedService = services.data.find(
        (item: ServiceInterface) => item.id === serviceId
      );
      const selectedProvider = providers.data.find(
        (item: ProviderInterface) => item.id === providerId
      );
      const providerParams: { service: number; provider: ProviderInterface } = {
        service: serviceId,
        provider: selectedProvider || defaultProvider
      };

      if (selectedLocation && selectedService) {
        dispatch(selectLocation(selectedLocation));
        dispatch(selectService(selectedService));
        dispatch(setProvider(providerParams));
        dispatch(setRedirectFlow(LocationFlowType.BOOK));

        if (selectedProvider) {
          dispatch(setStep(BookingSteps.TIME));
        } else {
          dispatch(setStep(BookingSteps.SERVICE));
        }
      } else {
        dispatch(resetForm());
      }
    } catch (error) {
      console.error(`Error while booking service again: ${error.message}`);
    } finally {
      dispatch(setIsBookingLoading(false));
    }
  };

export const moveAppointment =
  (appointment: UpcomingAppointment): AppThunk<Promise<ResponseStatusObject>> =>
  async (dispatch) => {
    try {
      dispatch(resetForm());
      dispatch(setIsBookingLoading(true));
      dispatch(setMoveApptId(appointment.confirmation_num));
      dispatch(setRedirectFlow(LocationFlowType.BOOK));

      const steps: number[] = [];

      const servicePromises = appointment.services.map(async (service) => {
        const locationId = appointment.store_num;
        const serviceId = service.svc_num;
        const providerId = service.provider_num;
        const categoryId = service.svc_cat_num;

        const [locations, services, providers, categories, addons] =
          await Promise.all([
            api.locations.getLocations(ContentFilter.ServicesOnly),
            api.services.getServicesByLocId(categoryId, locationId),
            api.services.getProviders(locationId, serviceId),
            api.services.getCategoriesByLocId(locationId),
            api.services.getAddons(locationId, serviceId)
          ]);

        dispatch(setLocations(locations.data));
        dispatch(
          setServices({ locationId, categoryId, services: services.data })
        );
        dispatch(
          setProviders({ locationId, serviceId, providers: providers.data })
        );
        dispatch(setCategories({ locationId, categories: categories.data }));
        dispatch(
          setAddonsForService({ locationId, serviceId, addons: addons.data })
        );

        const selectedLocation = locations.data.find(
          (item: LocationInterface) => item.id === locationId
        );
        const selectedService = services.data.find(
          (item: ServiceInterface) => item.id === serviceId
        );
        const selectedProvider = providers.data.find(
          (item: ProviderInterface) => item.id === providerId
        );
        const providerParams: { service: number; provider: ProviderInterface } =
          {
            service: serviceId,
            provider: selectedProvider || defaultProvider
          };

        if (selectedLocation && selectedService) {
          steps.push(2);
        } else {
          steps.push(0);
        }

        dispatch(selectLocation(selectedLocation));
        dispatch(selectService(selectedService));
        dispatch(setProvider(providerParams));
      });

      await Promise.all(servicePromises);
      const availableStep = steps.sort()[0] || 0;
      if (availableStep === 0) {
        dispatch(resetForm());
        return {
          isSuccess: false,
          message: "Can't move appointment. Please contact our service."
        };
      }
      dispatch(setRedirectFlow(LocationFlowType.BOOK));
      dispatch(setStep(availableStep));
      return {
        isSuccess: true,
        message: ''
      };
    } catch (error) {
      console.error(`Error while move appointment: ${error.message}`);
      return {
        isSuccess: false,
        message: error.message
      };
    } finally {
      dispatch(setIsBookingLoading(false));
    }
  };

export const geRecommendations =
  (id: number): AppThunk<Promise<ProductItem[]>> =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      const { data } = await api.services.geRecommendations(id);
      dispatch(setRecommendedProducts(data));

      return Array.isArray(data) ? data : [];
    } catch (error) {
      console.error(`Error while getting appointments: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      return [];
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const buyRecommendedProducts =
  (
    params: SelectedProductType[],
    apptId: number,
    nextHandler: () => void
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setIsLoading(true));
      await api.services.buyRecommendedProducts(params, apptId);

      nextHandler();
    } catch (error) {
      console.error(`Error while booking appointment: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
      return { isSuccess: false, apptId: '' };
    } finally {
      dispatch(setIsLoading(false));
    }
  };

export const bookServicesByParameters =
  (
    services: string[] | null,
    store: string | null,
    locations: LocationInterface[],
    setStoreIdToSearchParameters: (ids: string[]) => void
  ): AppThunk =>
  async (dispatch, getState) => {
    dispatch(setIsLoading(true));

    const {
      services: { addons },
      bookingAppointment: {
        selectedAddons,
        selectedProviders: storeSelectedProviders,
        selectedAppointment
      },
      persistAuth: { userInfo }
    } = getState();

    dispatch(setIsLoadingBookServicesByParameters(true));
    try {
      const singleLocation = locations.length < 2;

      const selectedLocation = await locations.find(
        (loc) => String(loc.id) === store
      );

      if (selectedLocation) {
        dispatch(selectLocation(selectedLocation));
        const { data } = await api.services.getServicesList(
          selectedLocation.id
        );

        const selectedServices = data.filter(
          (service: ServiceInterface) => services?.includes(String(service.id))
        );

        if (selectedServices.length !== services?.length) {
          const selectedServicesIds = selectedServices.map(
            (service: ServiceInterface) => String(service.id)
          );
          setStoreIdToSearchParameters(selectedServicesIds);
        }

        if (selectedAppointment) {
          return;
        }
        if (selectedServices.length !== 0) {
          dispatch(setRedirectFlow(LocationFlowType.BOOK));
          dispatch(selectServiceList(selectedServices));

          const addonsByLocation = addons[selectedLocation.id];
          const serviceIds: number[] = selectedServices.map(
            (service: ServiceInterface) => service.id
          );
          const selectedServicesAddons = serviceIds.filter(
            (id: number) => !!addonsByLocation?.[id]
          );

          const toFetch = serviceIds.filter(
            (id: number) => !selectedServicesAddons.includes(id)
          );
          const addonsData = await dispatch(
            getAddons(selectedLocation.id, toFetch)
          );

          const isAddonsExist = selectedServicesAddons
            .map((item) => addonsByLocation[item].length)
            .some(Boolean);

          const addonIds = Object.values(selectedAddons)
            .flat(2)
            .map((addon) => addon.id);
          const providerDecision = await dispatch(
            resolveProvidersForBooking(
              selectedLocation.id,
              serviceIds,
              addonIds
            )
          );

          if (addonsData?.length || isAddonsExist) {
            dispatch(setStep(BookingSteps.SERVICE));
            dispatch(setServiceActiveStep(BookServiceSteps.Addons));
          } else {
            const suggestions =
              (await dispatch(
                getSuggestedServices(selectedLocation.id, serviceIds)
              )) ?? [];

            if (
              shouldShowSuggestedServicesStep(selectedServices, suggestions)
            ) {
              dispatch(setStep(BookingSteps.SERVICE));
              dispatch(
                setServiceActiveStep(BookServiceSteps.SuggestedServices)
              );
              return;
            }

            const isSelected = Object.values(storeSelectedProviders).some(
              (prvdr) => prvdr?.id !== -1
            );

            if (storeSelectedProviders && isSelected) {
              dispatch(setServiceActiveStep(BookServiceSteps.Providers));
              dispatch(setStep(BookingSteps.TIME));
            } else if (providerDecision.action === 'show') {
              selectedServices.forEach((serviceItem: ServiceInterface) => {
                dispatch(
                  setProvider({
                    service: serviceItem.id,
                    provider:
                      storeSelectedProviders[serviceItem.id] ?? defaultProvider
                  })
                );
              });

              dispatch(setStep(BookingSteps.SERVICE));
              dispatch(setServiceActiveStep(BookServiceSteps.Providers));
            } else if (providerDecision.action === 'autoSelect') {
              providerDecision.selections.forEach(({ entityId, provider }) => {
                dispatch(
                  setProvider({
                    service: entityId,
                    provider
                  })
                );
              });
              dispatch(setStep(BookingSteps.TIME));
            } else if (selectedAppointment) {
              if (!userInfo) {
                return;
              }
              dispatch(setStep(BookingSteps.REVIEW));
            } else {
              dispatch(setStep(BookingSteps.TIME));
            }
          }
        } else {
          dispatch(setStep(BookingSteps.SERVICE));
          dispatch(selectServiceList([]));
        }
      } else if (singleLocation) {
        dispatch(selectLocation(locations[0]));
        dispatch(setStep(BookingSteps.SERVICE));
      }

      dispatch(setReferalLink(''));
    } catch (error) {
      console.error(`Error while booking appointment: ${error.message}`);
      dispatch(setError(error.response?.data?.error || error.message));
    } finally {
      dispatch(setIsLoadingBookServicesByParameters(false));
      dispatch(setIsLoading(false));
    }
  };

export const repeatBookingAppt =
  (appt: RecentAppointment, redirectTo: (url: string) => void): AppThunk =>
  async (dispatch, getState) => {
    const {
      locations: { locations },
      locationsApi: { queries }
    } = getState();

    dispatch(resetForm());
    dispatch(clearService());
    dispatch(setRedirectFlow(LocationFlowType.BOOK));
    dispatch(setStep(BookingSteps.SERVICE));
    dispatch(setServiceActiveStep(BookServiceSteps.Services));

    const locationList = locations ?? queries?.['getLocations(1)']?.data ?? [];
    const serviceLocation = locationList.find(
      (storeLocation) => storeLocation.id === appt?.sv_num
    );

    if (serviceLocation?.id) {
      dispatch(selectLocation(serviceLocation));
    }

    const apptIds = (appt?.services ?? []).map((svc) => svc.svc_num).join(',');
    const qs: string[] = [];
    if (isValidStoreIdFromAppt(appt?.store_num)) {
      qs.push(
        `${SearchParameters.Store}=${encodeURIComponent(
          String(appt.store_num)
        )}`
      );
    }
    if (apptIds) {
      qs.push(`${SearchParameters.Services}=${apptIds}`);
    }
    const path =
      qs.length > 0 ? `${ROUTES.booking}?${qs.join('&')}` : ROUTES.booking;

    redirectTo(path);
  };
